<template>
    <!-- Géolocalisation : latitude / longitude saisies, position de l'appareil, ou clic sur la carte (repère déplaçable) -->
    <div class="geolocation-picker">
        <div class="d-flex flex-wrap align-items-center">
            <div class="input-group input-group-sm geolocation-picker-field mr-2 mb-2">
                <div class="input-group-prepend"><span class="input-group-text">Lat.</span></div>
                <input type="number" step="0.0000001" min="-90" max="90" class="form-control" :class="{ 'is-invalid': invalid }" :name="latitudeName" v-model="lat" placeholder="48.8566140" @change="emitChange">
            </div>
            <div class="input-group input-group-sm geolocation-picker-field mr-2 mb-2">
                <div class="input-group-prepend"><span class="input-group-text">Long.</span></div>
                <input type="number" step="0.0000001" min="-180" max="180" class="form-control" :class="{ 'is-invalid': invalid }" :name="longitudeName" v-model="lng" placeholder="2.3522219" @change="emitChange">
            </div>
            <button type="button" class="ix-btn gray mb-2 mr-2" @click="locate" :disabled="locating" title="Utiliser la position de cet appareil">
                <i class="far" :class="locating ? 'fa-circle-notch fa-spin' : 'fa-location-arrow'"></i> Ma position
            </button>
            <button v-if="hasPosition" type="button" class="ix-btn transparent mb-2" @click="clear" title="Retirer la géolocalisation">
                <i class="far fa-times"></i> Effacer
            </button>
        </div>
        <small v-if="message" class="d-block mb-2" :class="messageError ? 'text-danger' : 'text-muted'">{{ message }}</small>

        <div class="geolocation-picker-map">
            <l-map ref="map" :zoom="zoom" :center="center" style="height: 100%" @click="onMapClick">
                <l-tile-layer :url="tileUrl" :attribution="attribution"></l-tile-layer>
                <l-marker v-if="hasPosition" :lat-lng="[parseFloat(lat), parseFloat(lng)]" :draggable="true" @dragend="onMarkerDrag"></l-marker>
            </l-map>
        </div>
        <small class="form-text text-muted">Cliquez sur la carte pour placer la zone, ou déplacez le repère.</small>
    </div>
</template>

<script>
import { Icon } from "leaflet";
import { LMap, LTileLayer, LMarker } from "vue2-leaflet";

// Icônes du repère servies par webpack (comme la carte des équipements)
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Centre par défaut : la France
const DEFAULT_CENTER = [46.6, 2.4];

export default {
    name: 'geolocation-picker',
    components: { LMap, LTileLayer, LMarker },
    props: {
        latitude: { default: null },
        longitude: { default: null },
        // Noms des champs pour un formulaire Blade (vides dans un composant Vue)
        latitudeName: { type: String, default: null },
        longitudeName: { type: String, default: null },
        // Affichée (ex. modale ouverte) : la carte recalcule sa taille
        visible: { type: Boolean, default: true },
        invalid: { type: Boolean, default: false },
    },
    data() {
        return {
            lat: this.format(this.latitude),
            lng: this.format(this.longitude),
            locating: false,
            message: '',
            messageError: false,
            tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        };
    },
    computed: {
        hasPosition() {
            return this.lat !== '' && this.lng !== '' && !isNaN(parseFloat(this.lat)) && !isNaN(parseFloat(this.lng));
        },
        center() {
            return this.hasPosition ? [parseFloat(this.lat), parseFloat(this.lng)] : DEFAULT_CENTER;
        },
        zoom() {
            return this.hasPosition ? 16 : 5;
        },
    },
    watch: {
        latitude(value) { this.lat = this.format(value); },
        longitude(value) { this.lng = this.format(value); },
        visible(value) {
            if (value) this.refreshMap();
        },
    },
    methods: {
        format(value) {
            return value === null || value === undefined || value === '' ? '' : String(value);
        },
        round(value) {
            return Math.round(value * 10000000) / 10000000;
        },
        setPosition(lat, lng) {
            this.lat = String(this.round(lat));
            this.lng = String(this.round(lng));
            this.emitChange();
        },
        emitChange() {
            this.$emit('change', {
                latitude: this.hasPosition ? parseFloat(this.lat) : null,
                longitude: this.hasPosition ? parseFloat(this.lng) : null,
            });
        },
        onMapClick(event) {
            this.setPosition(event.latlng.lat, event.latlng.lng);
        },
        onMarkerDrag(event) {
            const position = event.target.getLatLng();
            this.setPosition(position.lat, position.lng);
        },
        locate() {
            this.message = '';
            if (!navigator.geolocation) {
                this.message = "La géolocalisation n'est pas disponible sur ce navigateur.";
                this.messageError = true;
                return;
            }
            this.locating = true;
            navigator.geolocation.getCurrentPosition(position => {
                this.locating = false;
                this.setPosition(position.coords.latitude, position.coords.longitude);
                this.message = 'Position de l\'appareil reprise (précision ≈ ' + Math.round(position.coords.accuracy) + ' m).';
                this.messageError = false;
            }, error => {
                this.locating = false;
                this.message = error.code === 1 ? "Accès à la position refusé : autorisez la localisation pour ce site." : "Position introuvable pour le moment.";
                this.messageError = true;
            }, { enableHighAccuracy: true, timeout: 10000 });
        },
        clear() {
            this.lat = '';
            this.lng = '';
            this.message = '';
            this.emitChange();
        },
        // Carte affichée dans un conteneur jusque-là masqué (modale) : taille recalculée
        refreshMap() {
            this.$nextTick(() => {
                setTimeout(() => {
                    if (this.$refs.map && this.$refs.map.mapObject) {
                        this.$refs.map.mapObject.invalidateSize();
                        this.$refs.map.mapObject.setView(this.center, this.zoom);
                    }
                }, 200);
            });
        },
    },
    mounted() {
        this.refreshMap();
    },
};
</script>

<style>
    .geolocation-picker-field {
        width: 190px;
    }

    .geolocation-picker-map {
        height: 220px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        overflow: hidden;
    }
</style>
